import type React from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import type { LuminaSubAgentResponseType } from '@atlassian/lumina';
import { setSessionItem } from '@atlassian/search-dialog';
import type { SearchExperimentLayer } from '@atlassian/search-experiment';

import { SAINFeatureControl } from '../../common/utils/sain-feature-control';
import { type QueryIntent, SEARCH_AI_ANSWER_CACHE_KEY } from '../../constants';
import type { IntentClassificationData } from '../../query-intent-api/types';
import { StreamAnswerGenerationTypes } from '../../stream-api/types';
import { sha256Hash } from '../../utils';
import { type AIAnswerContextType } from '../ai-answer-context';
import { DEFAULT_EMPTY_NLP_SEARCH_STATE } from '../ai-answer-dialog';
import {
	getNonStreamFetchErrorEventPayload,
	getStreamFetchErrorEventPayload,
} from '../error-analytics';
import {
	type AIAnswerQueryFunctionType,
	type AIAnswerQueryStreamMarkdownFailedBackend,
	type AIAnswerQueryStreamResponse,
	AIAnswerQueryStreamType,
	type NLPDynamicLayoutSubAgentResponse,
	NLPSearchErrorCodes,
	NLPSearchErrorState,
	NLPSearchResultFormat,
	type NLPSearchType,
	type parsedFiltersType,
} from '../types';

import { checkSkipAIAnswerCall } from './smartAnswerRouteStrategy';
import { StreamFailedError } from './types';

export type FetchAIAnswerArgs = {
	cloudId: string;
	additional_context?: string;
	locale: string;
	query: string;
	source: string;
	followUpsEnabled?: boolean;
	getAIAnswer: AIAnswerQueryFunctionType | null;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	partialResponse: AIAnswerContextType['partialResponse'];
	partialResponseAbortControllerRef: React.MutableRefObject<AbortController | undefined>;
	setPartialResponse: React.Dispatch<React.SetStateAction<AIAnswerContextType['partialResponse']>>;
	setData: React.Dispatch<React.SetStateAction<NLPSearchType>>;
	setAnswerStreamed: React.Dispatch<React.SetStateAction<boolean>>;
	parsedFilters?: parsedFiltersType;
	selectedProduct?: string;
	isReadingAids: boolean;
	fireAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
	setQueryReasoning: React.Dispatch<
		React.SetStateAction<AIAnswerContextType['queryReasoning']> | undefined
	>;
	setDynamicLayoutSubAgentMessages: React.Dispatch<
		React.SetStateAction<NLPDynamicLayoutSubAgentResponse[] | LuminaSubAgentResponseType[]>
	>;
	setIsLuminaAnswer: React.Dispatch<boolean>;
	experimentContext?: {
		experimentLayers: SearchExperimentLayer[];
	};
	queryIntentClassificationData?: IntentClassificationData;
	referrer?: string | null;
	isHello: boolean;
	searchSessionId?: string;
	sourceProduct?: string;
	queryIntent?: QueryIntent;
};

export const fetchAIAnswer = async ({
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	cloudId,
	locale,
	query,
	additional_context,
	source,
	followUpsEnabled = false,
	getAIAnswer,
	setLoading,
	partialResponse,
	partialResponseAbortControllerRef,
	setPartialResponse,
	setData,
	setAnswerStreamed,
	parsedFilters,
	selectedProduct,
	isReadingAids,
	fireAnalyticsEvent,
	setQueryReasoning,
	setDynamicLayoutSubAgentMessages,
	setIsLuminaAnswer,
	experimentContext,
	queryIntentClassificationData,
	referrer,
	isHello,
	searchSessionId,
	sourceProduct,
	queryIntent,
}: FetchAIAnswerArgs): Promise<void> => {
	if (!getAIAnswer) {
		return;
	}
	if (partialResponse?.partialNlpResult) {
		// clean up any old partial responses
		setPartialResponse(undefined);
	}
	// end any old partial response streams
	partialResponseAbortControllerRef.current?.abort();
	const abortController = new AbortController();
	partialResponseAbortControllerRef.current = abortController;

	// Check if we can skip the AI answer call based on intent classification data.
	// If the intent already has the data we need (e.g., PersonIntent with persons[]),
	// use it directly instead of making another API call.
	if (fg('kd_fe_persons_intent_detection_els')) {
		const skipResult = checkSkipAIAnswerCall(queryIntentClassificationData);
		if (skipResult.skip) {
			setData(skipResult.data);
			setSessionItem(SEARCH_AI_ANSWER_CACHE_KEY, JSON.stringify({ query, data: skipResult.data }));
			setAnswerStreamed(true);
			setLoading(false);
			return;
		}
	}

	// For graphQL getAIAnswer fns we expect each product to pass their own cloudIdARI in order for this component to be more product agnostic
	const aiAnswer = getAIAnswer({
		variables: {
			query,
			additional_context,
			locale,
			filters: parsedFilters,
			selectedProduct,
			experience: isReadingAids ? 'readingAids' : source,
			followUpsEnabled,
			experimentContext,
			...(SAINFeatureControl.isZeroQuerySuggestionsV1Enabled(isHello) && referrer === 'zeroQuery'
				? { isQuerySuggestion: true }
				: {}),
		},
	});

	// Legacy version of processStreamResponse - used when feature gate is off
	const processStreamResponse = async (answer: AIAnswerQueryStreamResponse) => {
		try {
			let next = await answer.stream.next();
			let answerFormat;
			// Local variable to track if answer is Lumina format (used for cache enablement)
			let isLuminaAnswerLocal = false;

			while (!next.done) {
				// setLoading needed here for instances where stream gets interrupted and restarts, ensuring loading state is set while stream is active
				setLoading(true);
				const streamEvent = next.value;

				if (streamEvent.type === AIAnswerQueryStreamType.AnswerType) {
					answerFormat = streamEvent.message.format;
				}
				if (
					streamEvent.type === AIAnswerQueryStreamType.GenerationType &&
					SAINFeatureControl.canRenderLumina()
				) {
					if (streamEvent.message.answer_generation_type === StreamAnswerGenerationTypes.LUMINA) {
						isLuminaAnswerLocal = true;
						setIsLuminaAnswer(true);
					}
				}
				if (streamEvent.type === AIAnswerQueryStreamType.Trace) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}
					setQueryReasoning(streamEvent.message);
				}
				if (streamEvent.type === AIAnswerQueryStreamType.AnswerPart) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}

					setPartialResponse((previousPartialResponse) => {
						let previousPartialNlpResult: string = previousPartialResponse?.partialNlpResult || '';
						return {
							format: NLPSearchResultFormat.MARKDOWN,
							partialNlpResult: previousPartialNlpResult + streamEvent.message.nlpResult,
						};
					});
				}

				if (SAINFeatureControl.canRenderLumina()) {
					if (streamEvent.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage) {
						setDynamicLayoutSubAgentMessages((prevMessages) => [
							...prevMessages,
							streamEvent.message,
						]);
					}
				}

				if (streamEvent.type === AIAnswerQueryStreamType.FinalResponse) {
					setPartialResponse(undefined);
					setData(streamEvent.message);
					setLoading(false);
				}

				next = await answer.stream.next();
				if (abortController.signal.aborted) {
					// eslint-disable-next-line no-throw-literal
					throw 'Aborted';
				}
			}

			const streamEnd = next.value;
			if (abortController.signal.aborted) {
				// eslint-disable-next-line no-throw-literal
				throw streamEnd;
			}

			if (streamEnd.state === 'complete') {
				setPartialResponse(undefined);
				setData(streamEnd.value.message);
				// Only store to cache if not a Lumina answer
				if (isReadingAids || !isLuminaAnswerLocal) {
					setSessionItem(
						SEARCH_AI_ANSWER_CACHE_KEY,
						JSON.stringify({
							query: query,
							data: streamEnd.value.message,
						}),
					);
				}
			} else {
				if (streamEnd.reason === 'timeout') {
					setPartialResponse(undefined);
					setData({
						errorState: NLPSearchErrorState.TimeoutError,
						errorCode: 0,
						format: null,
						disclaimer: null,
						nlpResults: [],
						uniqueSources: [],
						nlpFollowUpResults: null,
					});
				} else {
					throw streamEnd;
				}
			}
		} catch (error: unknown) {
			fireAnalyticsEvent(getStreamFetchErrorEventPayload(error, source));
			if (error === 'Aborted') {
				return;
			}
			setPartialResponse(undefined);
			setData({
				errorState:
					typeof error === 'object' &&
					error !== null &&
					'details' in error &&
					error?.details === NLPSearchErrorCodes.HIPAA_ENABLED_ERROR
						? NLPSearchErrorState.HIPAAError
						: NLPSearchErrorState.NetworkError,
				errorCode: (error as AIAnswerQueryStreamMarkdownFailedBackend)?.errorCode
					? (error as AIAnswerQueryStreamMarkdownFailedBackend).errorCode
					: 0,
				format: null,
				disclaimer: null,
				nlpResults: [],
				uniqueSources: [],
				nlpFollowUpResults: null,
				nlpSuggestedQueries: [],
			});
		} finally {
			setAnswerStreamed(true);
			setLoading(false);
		}
	};

	// New version with improved error handling using standard JS Error objects
	const processStreamResponseImprovedErrorHandling = async (
		answer: AIAnswerQueryStreamResponse,
	) => {
		const fetchStartTime = Date.now();
		let streamEventsReceived = 0;

		try {
			let next = await answer.stream.next();
			let answerFormat;
			// Local variable to track if answer is Lumina format (used for cache enablement)
			let isLuminaAnswerLocal = false;

			while (!next.done) {
				// setLoading needed here for instances where stream gets interrupted and restarts, ensuring loading state is set while stream is active
				setLoading(true);
				streamEventsReceived++;
				const streamEvent = next.value;

				if (streamEvent.type === AIAnswerQueryStreamType.AnswerType) {
					answerFormat = streamEvent.message.format;
				}
				if (
					streamEvent.type === AIAnswerQueryStreamType.GenerationType &&
					SAINFeatureControl.canRenderLumina()
				) {
					if (streamEvent.message.answer_generation_type === StreamAnswerGenerationTypes.LUMINA) {
						isLuminaAnswerLocal = true;
						setIsLuminaAnswer(true);
					}
				}
				if (streamEvent.type === AIAnswerQueryStreamType.Trace) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}
					setQueryReasoning(streamEvent.message);
				}
				if (streamEvent.type === AIAnswerQueryStreamType.AnswerPart) {
					if (answerFormat !== NLPSearchResultFormat.MARKDOWN) {
						throw new Error(`Unexpected format: ${answerFormat}`);
					}

					setPartialResponse((previousPartialResponse) => {
						let previousPartialNlpResult: string = previousPartialResponse?.partialNlpResult || '';
						return {
							format: NLPSearchResultFormat.MARKDOWN,
							partialNlpResult: previousPartialNlpResult + streamEvent.message.nlpResult,
						};
					});
				}

				if (SAINFeatureControl.canRenderLumina()) {
					if (streamEvent.type === AIAnswerQueryStreamType.DynamicLayoutSubAgentMessage) {
						setDynamicLayoutSubAgentMessages((prevMessages) => [
							...prevMessages,
							streamEvent.message,
						]);
					}
				}

				if (streamEvent.type === AIAnswerQueryStreamType.FinalResponse) {
					setPartialResponse(undefined);
					const msg = streamEvent.message;
					setData(
						msg.errorState && msg.errorState !== NLPSearchErrorState.Default
							? {
									...msg,
									streamEventsReceived,
									networkErrorDuration: Date.now() - fetchStartTime,
								}
							: msg,
					);
					setLoading(false);
				}

				next = await answer.stream.next();
				if (abortController.signal.aborted) {
					throw new Error('Aborted');
				}
			}

			const streamEnd = next.value;
			if (abortController.signal.aborted) {
				throw new Error(streamEnd.state === 'failed' ? streamEnd.reason : 'Aborted');
			}

			if (streamEnd.state === 'complete') {
				setPartialResponse(undefined);
				setData(streamEnd.value.message);
				// Only store to cache if not a Lumina answer
				if (isReadingAids || !isLuminaAnswerLocal) {
					setSessionItem(
						SEARCH_AI_ANSWER_CACHE_KEY,
						JSON.stringify({
							query: query,
							data: streamEnd.value.message,
						}),
					);
				}
			} else {
				if (streamEnd.reason === 'timeout') {
					setPartialResponse(undefined);
					setData({
						errorState: NLPSearchErrorState.TimeoutError,
						errorCode: 0,
						format: null,
						disclaimer: null,
						nlpResults: [],
						uniqueSources: [],
						nlpFollowUpResults: null,
					});
				} else {
					throw new StreamFailedError(`Stream failed: ${streamEnd.reason}`, {
						details: 'details' in streamEnd ? streamEnd.details : undefined,
						errorCode: 'errorCode' in streamEnd ? streamEnd.errorCode : undefined,
						reason: streamEnd.reason,
						backendFailureType:
							'backendFailureType' in streamEnd ? streamEnd.backendFailureType : undefined,
					});
				}
			}
		} catch (error: unknown) {
			const networkErrorDuration = Date.now() - fetchStartTime;

			fireAnalyticsEvent(
				getStreamFetchErrorEventPayload(error, source, {
					searchSessionId,
					queryHash: sha256Hash(query),
					sourceProduct,
					isReadingAids,
					queryIntent,
				}),
			);
			setPartialResponse(undefined);

			if (error instanceof Error && error.message === 'Aborted') {
				if (fg('kd_fix_abort_errors')) {
					// Don't  set error data on client-initiated abort
					return;
				}
				setData({
					errorState: NLPSearchErrorState.NetworkError,
					networkErrorReason: 'client_aborted',
					streamEventsReceived,
					networkErrorDuration,
					format: null,
					disclaimer: null,
					nlpResults: [],
					uniqueSources: [],
					nlpFollowUpResults: null,
					nlpSuggestedQueries: [],
				});
				return;
			}

			const details = error instanceof StreamFailedError ? error.details : undefined;
			const errorCode = error instanceof StreamFailedError ? (error.errorCode ?? 0) : 0;
			const networkErrorReason = error instanceof StreamFailedError ? error.reason : undefined;
			const networkErrorDetails = error instanceof StreamFailedError ? error.details : undefined;
			const backendFailureType =
				error instanceof StreamFailedError ? error.backendFailureType : undefined;

			setData({
				errorState:
					details === NLPSearchErrorCodes.HIPAA_ENABLED_ERROR
						? NLPSearchErrorState.HIPAAError
						: NLPSearchErrorState.NetworkError,
				errorCode,
				networkErrorReason,
				networkErrorDetails,
				backendFailureType,
				streamEventsReceived,
				networkErrorDuration,
				format: null,
				disclaimer: null,
				nlpResults: [],
				uniqueSources: [],
				nlpFollowUpResults: null,
				nlpSuggestedQueries: [],
			});
		} finally {
			// Don't update loading/streamed state for aborted streams
			if (!abortController.signal.aborted || !fg('kd_fix_abort_errors')) {
				setAnswerStreamed(true);
				setLoading(false);
			}
		}
	};

	// If aiAnswer is an object with a type property -- it's a getAIAnswer
	// and we need to handle the new streaming response
	if ('type' in aiAnswer) {
		if (fg('kd_sain_improved_fetch_errors')) {
			processStreamResponseImprovedErrorHandling(aiAnswer);
		} else {
			processStreamResponse(aiAnswer);
		}
		return;
	}

	await aiAnswer
		.then((data) => {
			if ('type' in data) {
				processStreamResponse(data);
				return;
			}

			setData(data?.nlpSearch || DEFAULT_EMPTY_NLP_SEARCH_STATE);
			// Only store to cache if Lumina is disabled
			if (isReadingAids || !SAINFeatureControl.canRenderLumina()) {
				setSessionItem(
					SEARCH_AI_ANSWER_CACHE_KEY,
					JSON.stringify({
						query: query,
						data: data?.nlpSearch || DEFAULT_EMPTY_NLP_SEARCH_STATE,
					}),
				);
			}
			setLoading(false);
		})
		.catch((error: unknown) => {
			fireAnalyticsEvent(
				getNonStreamFetchErrorEventPayload(error, source, {
					searchSessionId,
					queryHash: sha256Hash(query),
					sourceProduct,
					isReadingAids,
					queryIntent,
				}),
			);
			const isTimeout = error instanceof DOMException && error.name === 'TimeoutError';
			setData({
				errorState: isTimeout ? NLPSearchErrorState.TimeoutError : NLPSearchErrorState.NetworkError,
				...(!isTimeout &&
					fg('kd_granular_network_errors') && {
						networkErrorReason: 'fetch_error',
						networkErrorDetails: error instanceof Error ? error.message : undefined,
					}),
				format: null,
				disclaimer: null,
				nlpResults: [],
				uniqueSources: [],
				nlpFollowUpResults: null,
			});
			setLoading(false);
		});
};
